import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Row, Col, Alert } from 'react-bootstrap';
import { importInvoiceItems } from '../http/invoiceAPI';
import { fetchStores, fetchCategories } from '../http/productAPI';

const typeOptions = [
  { value: 'shoe', label: 'Обувь' },
  { value: 'clothes', label: 'Одежда' },
  { value: 'toy', label: 'Игрушки' },
  { value: 'other', label: 'Прочее' }
];

const defaultRow = { name: '', purchasePrice: '', markup: 20, quantity: 1, type: 'other', store_id: '', barcode: '', size: '', color: '' };

const InvoiceImportModal = ({ show, onHide, invoiceId, onImported }) => {
  const [rows, setRows] = useState([ { ...defaultRow } ]);
  const [defaultMarkup, setDefaultMarkup] = useState(20);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStores().then(setStores).catch(()=>{});
    fetchCategories().then(setCategories).catch(()=>{});
  }, []);

  const addRow = () => setRows(prev => [...prev, { ...defaultRow, markup: defaultMarkup }]);
  const removeRow = (idx) => setRows(prev => prev.filter((_,i)=>i!==idx));
  const updateRow = (idx, field, value) => setRows(prev => prev.map((r,i)=> i===idx ? { ...r, [field]: value } : r));

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    const lines = text.split('\n').map(l=>l.trim()).filter(Boolean);
    if (!lines.length) return;
    const parsed = lines.map(line => {
      const parts = line.split(/\t|;/).map(p=>p.trim());
      return {
        ...defaultRow,
        name: parts[0] || '',
        quantity: Number(parts[1]) || 1,
        purchasePrice: parts[2] || '',
        markup: defaultMarkup,
        type: parts[3] || 'other'
      };
    });
    setRows(parsed);
  };

  const validRows = useMemo(() => rows.filter(r => r.name && Number(r.purchasePrice) > 0 && Number(r.quantity) > 0 && r.store_id), [rows]);

  const handleImport = async () => {
    if (!invoiceId) { setError('Не выбрана накладная'); return; }
    if (!validRows.length) { setError('Заполните товары: имя, сумма, количество и магазин'); return; }
    setError('');
    setLoading(true);
    try {
      const payload = {
        invoiceId,
        defaultMarkup,
        items: rows.map(r => ({
          name: r.name,
          purchasePrice: Number(r.purchasePrice)||0,
          quantity: Number(r.quantity)||0,
          markup: r.markup === '' ? defaultMarkup : Number(r.markup)||0,
          type: r.type,
          store_id: r.store_id,
          barcode: r.barcode,
          size: r.size,
          color: r.color,
          category_id: r.category_id
        }))
      };
      await importInvoiceItems(payload);
      if (onImported) onImported();
      onHide();
      setRows([ { ...defaultRow } ]);
    } catch (e) {
      setError(e?.response?.data?.message || 'Ошибка при импорте товаров');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Добавить товары из накладной</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Row className="mb-3 g-2">
          <Col md={3} sm={6} xs={12}>
            <Form.Label>Наценка по умолчанию (%)</Form.Label>
            <Form.Control type="number" value={defaultMarkup} onChange={e=>setDefaultMarkup(Number(e.target.value)||0)} />
          </Col>
          <Col md={9} sm={6} xs={12}>
            <Form.Label>Быстрый ввод (вставка строк: Название \t Кол-во \t Закупка \t Тип)</Form.Label>
            <Form.Control as="textarea" rows={2} placeholder="Вставьте строки из Excel/таблички" onPaste={handlePaste} />
          </Col>
        </Row>
        <div className="table-responsive" style={{ maxHeight: 360, overflow: 'auto' }}>
          <Table bordered hover size="sm" className="align-middle text-nowrap">
            <thead>
              <tr>
                <th>Название</th>
                <th>Кол-во</th>
                <th>Закупка</th>
                <th>Наценка %</th>
                <th>Тип</th>
                <th>Магазин</th>
                <th>Штрих-код</th>
                <th>Размер</th>
                <th>Цвет</th>
                <th>Категория</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx}>
                  <td><Form.Control value={r.name} onChange={e=>updateRow(idx,'name',e.target.value)} /></td>
                  <td style={{width:90}}><Form.Control type="number" min="0" value={r.quantity} onChange={e=>updateRow(idx,'quantity',e.target.value)} /></td>
                  <td style={{width:110}}><Form.Control type="number" min="0" step="0.01" value={r.purchasePrice} onChange={e=>updateRow(idx,'purchasePrice',e.target.value)} /></td>
                  <td style={{width:90}}><Form.Control type="number" value={r.markup} onChange={e=>updateRow(idx,'markup',e.target.value)} /></td>
                  <td style={{width:130}}>
                    <Form.Select value={r.type} onChange={e=>updateRow(idx,'type',e.target.value)}>
                      {typeOptions.map(opt=>(<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </Form.Select>
                  </td>
                  <td style={{width:160}}>
                    <Form.Select value={r.store_id} onChange={e=>updateRow(idx,'store_id',e.target.value)}>
                      <option value="">Магазин</option>
                      {stores.map(s=>(<option key={s.id} value={s.id}>{s.name}</option>))}
                    </Form.Select>
                  </td>
                  <td style={{width:120}}><Form.Control value={r.barcode} onChange={e=>updateRow(idx,'barcode',e.target.value)} /></td>
                  <td style={{width:100}}><Form.Control value={r.size} onChange={e=>updateRow(idx,'size',e.target.value)} placeholder="опц." /></td>
                  <td style={{width:100}}><Form.Control value={r.color} onChange={e=>updateRow(idx,'color',e.target.value)} placeholder="опц." /></td>
                  <td style={{width:160}}>
                    <Form.Select value={r.category_id||''} onChange={e=>updateRow(idx,'category_id',e.target.value)}>
                      <option value="">Без категории</option>
                      {categories.map(c=>(<option key={c.id} value={c.id}>{c.name}</option>))}
                    </Form.Select>
                  </td>
                  <td style={{width:60}}>
                    <Button variant="outline-danger" size="sm" onClick={()=>removeRow(idx)}>×</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="d-flex justify-content-between mt-3">
          <Button variant="outline-secondary" onClick={addRow}>Добавить строку</Button>
          <div className="text-muted small">Готово строк: {validRows.length} / {rows.length}</div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>Отмена</Button>
        <Button variant="primary" onClick={handleImport} disabled={loading}>Импортировать товары</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceImportModal;

